import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';

import { Cloudinary } from '@cloudinary/url-gen/instance/Cloudinary';
import { CloudinaryModule } from '@cloudinary/ng';
import { OAuthModule, OAuthModuleConfig } from 'angular-oauth2-oidc';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { QuillModule } from 'ngx-quill';
import { NgIdleModule } from '@ng-idle/core';
import { firstValueFrom } from 'rxjs';

import { routes } from 'src/app/app.routes';
import { provideAppStore } from 'src/app/modules/ngrx-store/store.config';

import { SettingsService } from 'src/app/modules/core/services/settings.service';
import { AuthInterceptor } from 'src/app/modules/shared/utilities/auth-interceptor.utils';

// NgRx Facades
import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';
import { ConstructionProgressFacade } from 'src/app/modules/ngrx-store/construction-progress/construction-progress.facade';
import { MessageFacade } from 'src/app/modules/ngrx-store/message/message.facade';
import { ServiceRequestsFacade } from 'src/app/modules/ngrx-store/service-requests/service-requests.facade';
import { TermsAndConditionsFacade } from 'src/app/modules/ngrx-store/terms-and-conditions/terms-and-conditions.facade';
import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';
import { RootFacade } from 'src/app/modules/ngrx-store/facade';

const initSettings = (settingsService: SettingsService, oAuthConfig: OAuthModuleConfig, cloudinary: Cloudinary) => async () => {
	const settings = await firstValueFrom(settingsService.getSettings());

	// Adds the apiUrl defined in the environment to the OAuthModuleConfig
	// which will automatically handle adding an auth header to any request made to that url

	oAuthConfig.resourceServer.allowedUrls?.push(settings.apiUrl);

	cloudinary.setConfig({
		cloud: {
			cloudName: settings.cloudinaryCloudName
		}
	});
	return settings;
}

const authConfigFactory = () => ({
	resourceServer: {
		allowedUrls: [],
		sendAccessToken: true,
	}
})

const cloudinaryConfigFactory = () => (
	new Cloudinary()
)

export const appConfig: ApplicationConfig = {
	providers: [
		// Zone change detection
		provideZoneChangeDetection(),

		// Router configuration
		provideRouter(
			routes,
			withComponentInputBinding(),
			withInMemoryScrolling(),
			withRouterConfig({
				paramsInheritanceStrategy: 'always', // Inherit parameters from parent routes
				canceledNavigationResolution: 'computed' // Returns to the same index in the session history that corresponds to the Angular route when navigation is canceled
			})
		),

		// Animations
		provideAnimations(),

		// HTTP Client
		provideHttpClient(withInterceptorsFromDi()),

		// NgRx Store
		...provideAppStore(),

		// OAuth Module
		importProvidersFrom(OAuthModule.forRoot()),

		// NgIdle Module
		importProvidersFrom(NgIdleModule.forRoot()),

		// Quill Module
		importProvidersFrom(QuillModule.forRoot()),

		// Cloudinary Module
		importProvidersFrom(CloudinaryModule),

		// App Initializer
		{
			provide: APP_INITIALIZER,
			useFactory: initSettings,
			deps: [SettingsService, OAuthModuleConfig, Cloudinary],
			multi: true
		},

		// OAuth Config
		{
			provide: OAuthModuleConfig,
			useFactory: authConfigFactory,
		},

		// Cloudinary Config
		{
			provide: Cloudinary,
			useFactory: cloudinaryConfigFactory
		},

		// HTTP Interceptors
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true
		},

		// NgxMask
		provideEnvironmentNgxMask({ validation: false }),

		// Material Form Field Default Options
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { appearance: 'outline' }
		},

		// NgRx Facades
		AppFacade,
		ConstructionProgressFacade,
		RootFacade,
		TermsAndConditionsFacade,
		YourHomeFacade,
		ServiceRequestsFacade,
		MessageFacade
	]
};
